<template>
  <div>
    <Head title="Panel de Control" />
    <h1 class="mb-8 text-3xl font-bold">Panel de Control</h1>

    <div v-if="role == 'admin'" class="grid grid-cols-1 gap-4 md:grid-cols-4">
      <box-dashboard 
        v-if="role == 'financial' || role == 'admin'" 
        :title="'Cuentas por cobrar'"
        :loading="loading"
      >
        <p class="text-center text-3xl font-black">
          <skeleton-loader v-if="loading" />
          <span v-else>${{ formatNumber(data.cuentas_cobrar) }}</span>
        </p>
      </box-dashboard>

      <box-dashboard 
        :title="'Ventas del mes'"
        :loading="loading"
      >
        <p class="text-center text-3xl font-black">
          <skeleton-loader v-if="loading" />
          <span v-else>${{ formatNumber(data.total_producido) }}</span>
        </p>
      </box-dashboard>

      <box-dashboard 
        :title="'Gastos del mes'"
        :loading="loading"
      >
        <p class="text-center text-3xl font-black">
          <skeleton-loader v-if="loading" />
          <span v-else>${{ formatNumber(data.gastos) }}</span>
        </p>
      </box-dashboard>
    </div>

    <section v-if="role == 'admin'" class="mt-8">
      <h3 class="text-xl font-bold mb-4">Estadísticas de productos</h3>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
        <box-dashboard 
          v-if="role == 'financial' || role == 'admin'" 
          :title="'Total de referencias'"
          :loading="loading"
        >
          <p class="text-center text-3xl font-black">
            <skeleton-loader v-if="loading" />
            <span v-else>{{ formatNumber(data.products) }}</span>
          </p>
        </box-dashboard>      

        <box-dashboard 
          v-if="role == 'financial' || role == 'admin'" 
          :title="'Costo total de inventario'"
          :loading="loading"
        >
          <p class="text-center text-3xl font-black">
            <skeleton-loader v-if="loading" />
            <span v-else>${{ formatNumber(data.productsStats.costTotal) }}</span>
          </p>
        </box-dashboard>

        <box-dashboard 
          :title="'Costo total de venta'"
          :loading="loading"
        >
          <p class="text-center text-3xl font-black">
            <skeleton-loader v-if="loading" />
            <span v-else>${{ formatNumber(data.productsStats.priceTotal) }}</span>
          </p>
        </box-dashboard>

        <box-dashboard 
          :title="'Utilidad en productos'"
          :loading="loading"
        >
          <p class="text-center text-3xl font-black">
            <skeleton-loader v-if="loading" />
            <span v-else>${{ formatNumber(data.productsStats.utilityTotal) }}</span>
          </p>
        </box-dashboard>
      </div>
    </section>

    <section v-if="role == 'admin'" class="mt-8">
      <h3 class="text-xl font-bold mb-4">Productos vendidos por categoría</h3>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
        <box-dashboard 
          v-for="category in data.categories" 
          :key="category.id" 
          :title="category.name"
          :loading="loading"
        >
          <p class="text-center text-3xl font-black">
            <skeleton-loader v-if="loading" />
            <span v-else>{{ formatNumber(category.total_sold) }}</span>
          </p>
        </box-dashboard>
      </div>
    </section>

    <section v-if="role == 'admin'" class="mt-8">
      <h3 class="text-xl font-bold mb-4">Capital por categoría</h3>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
        <box-dashboard 
          v-for="category in data.categoryCapitals" 
          :key="category.id" 
          :title="category.name"
          :loading="loading"
        >
          <p class="text-center text-3xl font-black">
            <skeleton-loader v-if="loading" />
            <span v-else>${{ formatNumber(category.cost) }}</span>
          </p>
        </box-dashboard>
      </div>
    </section>

    <section v-if="role == 'admin'" class="mt-8">
      <h3 class="text-xl font-bold mb-4">Accesos directos</h3>
      <div class="flex flex-wrap gap-3">
        <router-link 
          v-for="(link, index) in quickLinks" 
          :key="index"
          :to="link.path"
          class="bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded transition duration-200"
        >
          {{ link.name }}
        </router-link>
      </div>
    </section>

    <section v-if="role == 'admin' || role == 'sales' || role == 'financial'" class="mt-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-bold flex items-center gap-2">
          <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          Productos por terminarse (Piso de inventario)
        </h3>
        <span v-if="data.products_low_stock" class="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
          {{ data.products_low_stock.total ?? data.products_low_stock.length }} ítems con bajo stock
        </span>
      </div>

      <div class="bg-white rounded-lg shadow-md overflow-x-auto">
        <div v-if="loading">
          <skeleton-loader />
        </div>
        <div v-else-if="!data.products_low_stock || (data.products_low_stock.data ? data.products_low_stock.data.length === 0 : data.products_low_stock.length === 0)" class="p-6 text-center text-gray-500">
          No hay productos con stock por debajo del límite mínimo.
        </div>
        <div v-else>
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tienda / Depósito</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Actual</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Piso Mínimo</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(item, index) in lowStockItems" :key="item.id || index" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-600">{{ item.code }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ item.name }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span class="font-semibold">{{ item.store_name }}</span> ({{ item.deposit_name }})
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-bold" :class="item.current_stock === 0 ? 'text-red-600' : 'text-amber-600'">
                  {{ item.current_stock }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.min_stock }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span v-if="item.current_stock === 0" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    Agotado
                  </span>
                  <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                    Poco Stock
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Controles de paginación piso -->
          <div v-if="data.products_low_stock && data.products_low_stock.last_page > 1" class="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div class="text-xs text-gray-500">
              Página {{ data.products_low_stock.current_page }} de {{ data.products_low_stock.last_page }} (Total: {{ data.products_low_stock.total }})
            </div>
            <div class="flex space-x-1">
              <button 
                :disabled="data.products_low_stock.current_page === 1" 
                @click="fetchLowStockPage(data.products_low_stock.current_page - 1)"
                class="px-3 py-1 border rounded text-xs font-medium bg-white disabled:opacity-50 hover:bg-gray-100 transition"
              >
                Anterior
              </button>
              <button 
                :disabled="data.products_low_stock.current_page === data.products_low_stock.last_page" 
                @click="fetchLowStockPage(data.products_low_stock.current_page + 1)"
                class="px-3 py-1 border rounded text-xs font-medium bg-white disabled:opacity-50 hover:bg-gray-100 transition"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Productos en exceso (Techo de inventario) -->
    <section v-if="role == 'admin' || role == 'sales' || role == 'financial'" class="mt-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-bold flex items-center gap-2">
          <svg class="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
          </svg>
          Productos en exceso (Techo de inventario)
        </h3>
        <span v-if="data.products_high_stock" class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
          {{ data.products_high_stock.total ?? data.products_high_stock.length }} ítems sobre el techo
        </span>
      </div>

      <div class="bg-white rounded-lg shadow-md overflow-x-auto">
        <div v-if="loading">
          <skeleton-loader />
        </div>
        <div v-else-if="!data.products_high_stock || (data.products_high_stock.data ? data.products_high_stock.data.length === 0 : data.products_high_stock.length === 0)" class="p-6 text-center text-gray-500">
          No hay productos que superen el límite máximo de stock.
        </div>
        <div v-else>
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tienda / Depósito</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Actual</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Techo Máx.</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exceso</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(item, index) in highStockItems" :key="item.id || index" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-600">{{ item.code }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ item.name }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span class="font-semibold">{{ item.store_name }}</span> ({{ item.deposit_name }})
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-600">
                  {{ item.current_stock }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.max_stock }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-700">+{{ item.excess }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                    En Exceso
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Controles de paginación techo -->
          <div v-if="data.products_high_stock && data.products_high_stock.last_page > 1" class="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div class="text-xs text-gray-500">
              Página {{ data.products_high_stock.current_page }} de {{ data.products_high_stock.last_page }} (Total: {{ data.products_high_stock.total }})
            </div>
            <div class="flex space-x-1">
              <button 
                :disabled="data.products_high_stock.current_page === 1" 
                @click="fetchHighStockPage(data.products_high_stock.current_page - 1)"
                class="px-3 py-1 border rounded text-xs font-medium bg-white disabled:opacity-50 hover:bg-gray-100 transition"
              >
                Anterior
              </button>
              <button 
                :disabled="data.products_high_stock.current_page === data.products_high_stock.last_page" 
                @click="fetchHighStockPage(data.products_high_stock.current_page + 1)"
                class="px-3 py-1 border rounded text-xs font-medium bg-white disabled:opacity-50 hover:bg-gray-100 transition"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="role == 'admin' || role == 'sales'" class="mt-8">
      <h3 class="text-xl font-bold mb-4">Últimas ventas</h3>
      <div class="bg-white rounded-lg shadow-md overflow-x-auto">
        <div v-if="loading">
          <skeleton-loader />
        </div>
        <table v-else class="min-w-full">
          <thead>
            <tr>
              <th v-for="header in orderHeaders" :key="header.value" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {{ header.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in data.orders.data" :key="order.id" @click="navigateToOrder(order)" class="hover:bg-gray-50 cursor-pointer">
              <td class="px-6 py-4 whitespace-nowrap">{{ getDate(order.created_at) }}</td>
              <td class="px-6 py-4 whitespace-nowrap">{{ order.number_order }}</td>
              <td class="px-6 py-4 whitespace-nowrap">{{ order.client.name }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <order-status :status="order.order_state" />
              </td>
              <td class="px-6 py-4 whitespace-nowrap">{{ order.responsible.first_name }} {{ order.responsible.last_name }}</td>
              <td class="px-6 py-4 whitespace-nowrap">${{ formatNumber(order.balance) }}</td>
              <td class="px-6 py-4 whitespace-nowrap">${{ formatNumber(order.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import BoxDashboard from '../../Shared/BoxDashboard.vue'
import OrderStatus from '@/Shared/OrderStatus.vue'
import SkeletonLoader from '@/Shared/SkeletonLoader.vue'
import axios from 'axios';

export default {
  name: 'Dashboard',
  components: {
    Link,
    Head,
    BoxDashboard,
    OrderStatus,
    SkeletonLoader
  },
  layout: Layout,

  data() {
    return {
      loading: true,
      role: this.$parent.auth.user.role,
      data: {},
      lowPage: 1,
      highPage: 1,
      orderHeaders: [
        { text: 'Fecha', value: 'created_at' },
        { text: 'Número', value: 'number_order' },
        { text: 'Cliente', value: 'client.name' },
        { text: 'Estado', value: 'order_state' },
        { text: 'Vendedor', value: 'responsible' },
        { text: 'Abonado', value: 'balance' },
        { text: 'Total', value: 'total' }
      ],
      quickLinks: [
        { name: 'Tiendas', path: '/organizations' },
        { name: 'Depósitos', path: '/deposits' },
        { name: 'Productos', path: '/products' },
        { name: 'Marcas', path: '/brands' },
        { name: 'Inventario', path: '/inventories' },
        { name: 'Ventas', path: '/orders' },
        { name: 'Gastos', path: '/spents' },
        { name: 'Tasas de cambio', path: '/taxes' }
      ]
    }
  },

  computed: {
    lowStockItems() {
      if (!this.data.products_low_stock) return []
      const list = this.data.products_low_stock.data || this.data.products_low_stock
      return Array.isArray(list) ? list.filter(item => item !== null && item !== undefined) : []
    },
    highStockItems() {
      if (!this.data.products_high_stock) return []
      const list = this.data.products_high_stock.data || this.data.products_high_stock
      return Array.isArray(list) ? list.filter(item => item !== null && item !== undefined) : []
    }
  },

  mounted() {
    this.getData()
  },

  methods: {
    getData(lowPage = 1, highPage = 1) {
      axios.get('/data', {
        params: {
          low_page: lowPage,
          high_page: highPage
        }
      })
        .then(response => {
          this.data = response.data
          this.loading = false
        })
    },

    fetchLowStockPage(page) {
      this.lowPage = page
      this.getData(this.lowPage, this.highPage)
    },

    fetchHighStockPage(page) {
      this.highPage = page
      this.getData(this.lowPage, this.highPage)
    },

    formatNumber(value) {
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },

    getDate(date) {
      return new Date(date).toLocaleString('es-VE', { 
        hour12: false,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    navigateToOrder(order) {
      this.$router.push(`/orders/${order.id}`)
    }
  }
}
</script>
